// src/pages/Solicitacoes/Solicitacoes.jsx
import React, {useState, useEffect, useRef, useMemo} from 'react';
import {subMonths, format} from 'date-fns';
import {
    Box,
    Divider,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Fab,
    Typography,
    TextField,
    IconButton,
    Button,
    Chip,
    Backdrop,
    CircularProgress,
    Stack,
} from '@mui/material';
import OrderServiceModal from '../OrderService/OrderServiceModal';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoadingScreen from '../../components/common/loading';
import {Title} from '../../components/ui/Title';
import {Subtitle} from '../../components/ui/subtitle';
import {
    fetchOrdensServico,
    encerraOS,
    geraInsumos,
    excluirInsumosOS,
    enviarAnexoNotaPagamento,
    anexarArquivosNfOs,
} from '../../services/easyhubQueries';
import corrigirVirgulasJsonInvalido from '../../tools/fixcomma';
import '../../styles/Fornecedor.css';
import StatusLegend from './components/StatusLegend';
import HiddenFileInputs from './components/HiddenFileInputs';
import SolicitacoesModal from './components/SolicitacoesModal';
import StyledAccordion from './components/StyledAccordion';
import ContainerPaper from './components/ContainerPaper';

export default function Solicitacoes() {
    const [notasServico, setNotasServico] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const theme = useTheme();

    // menu/modal…
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('');
    const [currentNota, setCurrentNota] = useState(null);
    const [agreeToClose, setAgreeToClose] = useState(false);
    const [selectedFotos, setSelectedFotos] = useState([]);
    const [selectedNfs, setSelectedNfs] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [osModalOpen, setOsModalOpen] = useState(false);
    // estados para filtro de data: hoje e 3 meses atrás
    const hoje = new Date();
    const defaultEnd = format(hoje, 'yyyy-MM-dd');
    const defaultStart = format(subMonths(hoje, 3), 'yyyy-MM-dd');
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    // estado das OS recém criadas
    const [recentOS, setRecentOS] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('recentOS') || '[]');
        } catch {
            return [];
        }
    });

    const inputImagemRef = useRef(null);
    const inputNfRef = useRef(null);

    // extrai função de carregamento para chamar de novo após encerrar
    const loadData = async () => {
        setIsLoading(true);
        try {
            // converte "yyyy-MM-dd" → "yyyyMMdd"
            const ini = startDate.replace(/-/g, '');
            const fim = endDate.replace(/-/g, '');
            const raw = await fetchOrdensServico(ini, fim);
            const corrigido = corrigirVirgulasJsonInvalido(raw);
            const response =
                typeof corrigido === 'string'
                    ? (() => {
                          try {
                              return JSON.parse(corrigido);
                          } catch {
                              return corrigido;
                          }
                      })()
                    : corrigido;
            console.log('Ordens de serviço:', response);
            if (response.Retorno === 'OK' && Array.isArray(response.Ordens)) {
                const ordens = response.Ordens.map((o, i) => ({
                    id: `${o.Ordem}-${o.Bem}-${i}`,
                    numeroOS: o.Ordem,
                    servico: o.Servico,
                    bem: o.Bem,
                    data: o.dat_apro,
                    dtAbertura: o.dt_abertura?.trim() || '',
                    valorOrcamento: o.valor_tot,
                    valorAprovado: o.valor_tot_apr,
                    valorUnitario: o.valor_uni,
                    dataAprovacao: o.dat_apro,
                    aprovadoPor: o.nome_apr,
                    pedidoCompra: o.ped_com,
                    codigo: o.codigo?.trim() || '',
                    seqTar: o.seq_tar?.trim() || '',
                    descricao: o.desc_cod?.trim() || '',
                    quantidade: o.quanti || '',
                    dtEnc: o.dt_enc?.trim() || '',
                    status:
                        o.status_apr === '1'
                            ? 'Pendente'
                            : o.status_apr === '2'
                              ? 'Aprovado'
                              : o.status_apr === '3'
                                ? 'Rejeitado'
                                : 'Pendente',
                    temAnexos: false,
                    fotosAnexadas: [],
                    nfsAnexadas: [],
                }));
                setNotasServico(ordens);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const gruposAbertos = useMemo(() => {
        const map = {};
        notasServico
            .filter((n) => !n.dtEnc)
            .forEach((n) => {
                const key = `${n.numeroOS}-${n.bem}`;
                if (!map[key]) {
                    map[key] = {
                        key,
                        numeroOS: n.numeroOS,
                        bem: n.bem,
                        dtAbertura: n.dtAbertura,
                        items: [],
                    };
                }
                map[key].items.push(n);
            });
        return Object.values(map);
    }, [notasServico]);

    const gruposEncerrados = useMemo(() => {
        const map = {};
        notasServico
            .filter((n) => !!n.dtEnc)
            .forEach((n) => {
                const key = `${n.numeroOS}-${n.bem}`;
                if (!map[key]) {
                    map[key] = {
                        key,
                        numeroOS: n.numeroOS,
                        bem: n.bem,
                        dtAbertura: n.dtAbertura,
                        items: [],
                    };
                }
                map[key].items.push(n);
            });
        return Object.values(map);
    }, [notasServico]);

    // se uma OS recente aparecer em aberto ou encerrado, remove de recentOS
    useEffect(() => {
        const todas = new Set([
            ...gruposAbertos.map((g) => g.numeroOS),
            ...gruposEncerrados.map((g) => g.numeroOS),
        ]);
        const filtradas = recentOS.filter((r) => !todas.has(r.numeroOS));
        if (filtradas.length !== recentOS.length) {
            setRecentOS(filtradas);
            localStorage.setItem('recentOS', JSON.stringify(filtradas));
        }
    }, [gruposAbertos, gruposEncerrados, recentOS]);

    // helper para adicionar
    const addRecentOS = (numeroOS, bem) => {
        const entry = {numeroOS, bem, createdAt: Date.now()};
        const updated = [entry, ...recentOS];
        setRecentOS(updated);
        localStorage.setItem('recentOS', JSON.stringify(updated));
    };

    function handleMenuClick(e, id) {
        setAnchorEl(e.currentTarget);
        setMenuOpenId(id);
    }
    function handleMenuClose() {
        setAnchorEl(null);
        setMenuOpenId(null);
    }
    async function handleMenuAction(n, mode) {
        setCurrentNota(n);
        setDialogMode(mode);
        setSelectedFotos([]);
        setSelectedNfs([]);
        setAgreeToClose(false);
        setTabIndex(0);
        handleMenuClose();
        if (mode === 'alterar') {
            setDialogOpen(true);
        } else if (mode === 'excluir') {
            if (window.confirm(`Excluir o insumo ${n.descricao}?`)) {
                try {
                    console.log('Excluindo insumo:', n.seqTar);
                    setDeletingId(n.id);
                    await excluirInsumosOS(n.numeroOS, [{produto: n.codigo, seq: n.seqTar}]);
                    await loadData();
                } catch (err) {
                    console.error(err);
                } finally {
                    setDeletingId(null);
                }
            }
        } else if (mode === 'anexarImagens') {
            setTimeout(() => inputImagemRef.current?.click(), 0);
        } else if (mode === 'anexarNfs') {
            setTimeout(() => inputNfRef.current?.click(), 0);
        } else {
            setDialogOpen(true);
        }
    }
    function handleDialogClose() {
        setDialogOpen(false);
        setCurrentNota(null);
        setDialogMode('');
    }
    function handleFotosChange(e) {
        setSelectedFotos(Array.from(e.target.files));
    }
    function handleNfsChange(e) {
        setSelectedNfs(Array.from(e.target.files));
    }
    async function handleSubmit() {
        console.log('📤 handleSubmit disparado', {mode: dialogMode, selectedNfs});
        try {
            // 1) Grupo inteiro (LOCW027)
            if (dialogMode === 'anexarNotaFiscalGrupo' && currentNota) {
                console.log('📁 anexarNFGrupo para OS', currentNota.pedidoCompra);
                // extrai o pedido de compra do próprio grupo (do first item, solicitado pelo Lui)
                const pedido = currentNota.pedidoCompra || currentNota.items?.[0]?.pedidoCompra;
                console.log('📁 anexarNFGrupo para Pedido Compra', pedido);
                const anexos = await Promise.all(
                    selectedNfs.map(
                        (file) =>
                            new Promise((res, rej) => {
                                const reader = new FileReader();
                                reader.onload = () =>
                                    res({name: file.name, Img: reader.result.split(',')[1]});
                                reader.onerror = rej;
                                reader.readAsDataURL(file);
                            }),
                    ),
                );
                const resp = await anexarArquivosNfOs(pedido, anexos);
                console.log('✅ anexarArquivosNfOs response:', resp);
                await loadData();
                return handleDialogClose();
            }
            // 2) Item‐level attachments (LOCW028): fotos, imagens, “arquivos” ou nfs no item
            if (
                ['anexarFotos', 'anexarImagens', 'anexarArquivos', 'anexarNfs'].includes(
                    dialogMode,
                ) &&
                currentNota
            ) {
                const files = [...selectedFotos, ...selectedNfs];
                if (files.length === 0) return;

                const anexos = await Promise.all(
                    files.map(
                        (file) =>
                            new Promise((res, rej) => {
                                const reader = new FileReader();
                                reader.onload = () =>
                                    res({name: file.name, Img: reader.result.split(',')[1]});
                                reader.onerror = rej;
                                reader.readAsDataURL(file);
                            }),
                    ),
                );

                const resp = await enviarAnexoNotaPagamento(
                    currentNota.numeroOS,
                    currentNota.codigo,
                    currentNota.seqTar,
                    anexos,
                );
                console.log('✅ enviarAnexoNotaPagamento response:', resp);
                await loadData();
                return handleDialogClose();
            }
        } catch (err) {
            console.error('❌ erro em handleSubmit:', err);
        }
        if (dialogMode === 'encerrar' && currentNota) {
            setNotasServico((prev) =>
                prev.map((n) => (n.id === currentNota.id ? {...n, status: 'Encerrado'} : n)),
            );
        }
        handleDialogClose();
    }
    function handleExcluirAnexo(tipo, id) {}

    function podeMostrarMenu(status) {
        return ['Pendente', 'Aprovado', 'Rejeitado'].includes(status);
    }

    if (isLoading) return <LoadingScreen message="Carregando ordens de serviço..." />;

    return (
        <>
            <Box sx={{maxWidth: 960, mx: 'auto'}}>
                <ContainerPaper elevation={2} color={theme.palette.primary.main}>
                    {/* header com filtro de data */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                        }}
                    >
                        <Box>
                            <Title>Painel OS</Title>
                            <StatusLegend />
                        </Box>

                        <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                            <TextField
                                label="Data início"
                                type="date"
                                size="small"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{shrink: true}}
                            />
                            <TextField
                                label="Data fim"
                                type="date"
                                size="small"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{shrink: true}}
                            />
                            <IconButton color="primary" onClick={loadData}>
                                <SearchIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <Divider sx={{my: 2}} />

                    {/* só mostra se tiver alguma OS recente */}
                    {recentOS.length > 0 && (
                        <Accordion
                            defaultExpanded
                            sx={{mb: 2, boxShadow: 1, borderRadius: 2, overflow: 'hidden'}}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{'px': 0, '& .MuiAccordionSummary-content': {mx: 2}}}
                            >
                                <Subtitle sx={{width: '100%', textAlign: 'center'}}>
                                    Ordens recém criadas ({recentOS.length})
                                </Subtitle>
                            </AccordionSummary>
                            <AccordionDetails sx={{p: 0}}>
                                {recentOS.map(({numeroOS, bem}, idx) => (
                                    <Box
                                        key={`${numeroOS}-${idx}`}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto',
                                            alignItems: 'center',
                                            gap: 5,
                                            px: 2,
                                            py: 1.5,
                                            justifySelf: 'anchor-center',
                                            borderBottom:
                                                idx < recentOS.length - 1
                                                    ? `1px solid ${theme.palette.grey[300]}`
                                                    : 'none',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 5,
                                                alignItems: 'center',
                                            }}
                                        >
                                            {/* chip de número da OS */}
                                            <Chip
                                                label={`OS ${numeroOS}`}
                                                size="small"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    bgcolor: theme.palette.grey[100],
                                                }}
                                            />
                                            {/* chip de bem */}
                                            <Chip
                                                label={`Bem ${bem}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                // reusar modal de adicionar item
                                                setCurrentNota({numeroOS, bem});
                                                setDialogMode('adicionarItem');
                                                setDialogOpen(true);
                                            }}
                                        >
                                            Adicionar Insumo
                                        </Button>
                                    </Box>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Principal “Aberto / Encerrado” */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Subtitle sx={{width: '100%', textAlign: 'center'}}>
                                Ordens em aberto ({gruposAbertos.length})
                            </Subtitle>
                        </AccordionSummary>
                        <AccordionDetails>
                            {gruposAbertos.map((g) => (
                                <StyledAccordion
                                    key={`aberto-${g.key}`}
                                    group={g}
                                    anchorEl={anchorEl}
                                    menuOpenId={menuOpenId}
                                    handleMenuClick={handleMenuClick}
                                    handleMenuClose={handleMenuClose}
                                    handleMenuAction={handleMenuAction}
                                    podeMostrarMenu={podeMostrarMenu}
                                    handleGroupAction={(group, action) => {
                                        if (action === 'addItem') {
                                            setCurrentNota(group);
                                            setDialogMode('adicionarItem');
                                            setDialogOpen(true);
                                        } else if (action === 'closeGroup') {
                                            if (window.confirm(`Encerrar OS ${group.numeroOS}?`)) {
                                                encerraOS(group.numeroOS)
                                                    .then(() => loadData())
                                                    .catch(console.error);
                                            }
                                        } else if (action === 'anexarNotaFiscal') {
                                            // abre modal de anexar NF para essa OS
                                            // novo: abrimos modal no modo OS-level (os inteira)
                                            setCurrentNota(group);
                                            setDialogMode('anexarNotaFiscalGrupo');
                                            setSelectedNfs([]);
                                            setDialogOpen(true);
                                        }
                                    }}
                                />
                            ))}
                        </AccordionDetails>
                    </Accordion>

                    <Divider sx={{my: 4}} />

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Subtitle sx={{width: '100%', textAlign: 'center'}}>
                                Ordens encerradas ({gruposEncerrados.length})
                            </Subtitle>
                        </AccordionSummary>
                        <AccordionDetails>
                            {gruposEncerrados.map((g) => (
                                <StyledAccordion
                                    key={`encerrado-${g.key}`}
                                    group={g}
                                    anchorEl={anchorEl}
                                    menuOpenId={menuOpenId}
                                    handleMenuClick={handleMenuClick}
                                    handleMenuClose={handleMenuClose}
                                    handleMenuAction={handleMenuAction}
                                    podeMostrarMenu={podeMostrarMenu}
                                    // só para anexo de NF em grupo encerrado
                                    handleGroupAction={(group, action) => {
                                        if (action === 'anexarNotaFiscal') {
                                            setCurrentNota(group);
                                            setDialogMode('anexarNotaFiscalGrupo');
                                            setSelectedNfs([]);
                                            setDialogOpen(true);
                                        }
                                    }}
                                />
                            ))}
                        </AccordionDetails>
                    </Accordion>
                </ContainerPaper>
            </Box>

            <HiddenFileInputs
                inputImagemRef={inputImagemRef}
                inputNfRef={inputNfRef}
                handleFotosChange={handleFotosChange}
                handleNfsChange={handleNfsChange}
            />
            {/* Floating Action Button fixo */}
            <Fab
                color="primary"
                aria-label="nova-os"
                onClick={() => setOsModalOpen(true)}
                sx={{
                    position: 'fixed',
                    bottom: (theme) => theme.spacing(2),
                    right: (theme) => theme.spacing(2),
                }}
            >
                <AddIcon />
            </Fab>

            {/* Modal de geração de OS */}
            <OrderServiceModal
                open={osModalOpen}
                onClose={() => setOsModalOpen(false)}
                onSuccess={(numeroOS, bem) => {
                    addRecentOS(numeroOS, bem);
                }}
            />

            <SolicitacoesModal
                open={dialogOpen}
                onClose={handleDialogClose}
                mode={dialogMode}
                currentNota={currentNota}
                agreeToClose={agreeToClose}
                setAgreeToClose={setAgreeToClose}
                tabIndex={tabIndex}
                setTabIndex={setTabIndex}
                selectedFotos={selectedFotos}
                selectedNfs={selectedNfs}
                handleFotosChange={handleFotosChange}
                handleNfsChange={handleNfsChange}
                handleSubmit={handleSubmit}
                handleExcluirAnexo={handleExcluirAnexo}
                reloadData={loadData}
            />
        </>
    );
}
